import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useMe } from '../../api/queries';
import { Ic } from '../../components/Ic';
import { Seg } from '../../components/Seg';
import { WebhooksTab } from './tabs/WebhooksTab';
import { MembersTab } from './tabs/MembersTab';
import { DeployTab } from './tabs/DeployTab';
import { EnvironmentTab } from './tabs/EnvironmentTab';
import { AlertsTab } from './tabs/AlertsTab';
import { BulkTab } from './tabs/BulkTab';

const TABS = ['webhooks', 'members', 'deploy', 'environment', 'alerts', 'bulk'] as const;
type Tab = (typeof TABS)[number];

const isTab = (v: string | undefined): v is Tab => !!v && (TABS as readonly string[]).includes(v);

export function SettingsView() {
  const navigate = useNavigate();
  const { tab } = useParams();
  const { data: me } = useMe();

  if (!tab) return <Navigate to="/settings/webhooks" replace />;
  if (!isTab(tab)) return <Navigate to="/settings/webhooks" replace />;

  // Members is super-admin gated server-side (403). Hiding it avoids offering
  // a tab that can only fail — the server stays authoritative either way.
  const options = [
    { value: 'webhooks' as const, label: 'Webhooks', icon: Ic.webhook },
    ...(me?.is_super_admin ? [{ value: 'members' as const, label: 'Members', icon: Ic.user }] : []),
    { value: 'deploy' as const, label: 'Deploy', icon: Ic.upload },
    { value: 'environment' as const, label: 'Environment', icon: Ic.settings },
    { value: 'alerts' as const, label: 'Alerts', icon: Ic.bell },
    { value: 'bulk' as const, label: 'Bulk ops', icon: Ic.cmd },
  ];

  return (
    <div className="view-pad">
      <div className="sec-head">
        <h2>Settings</h2>
        <span className="spacer" />
        <Seg
          value={tab}
          onChange={(next) => navigate(`/settings/${next}`)}
          options={options}
          ariaLabel="Settings section"
        />
      </div>

      <div className="set-wrap">
        {tab === 'webhooks' && <WebhooksTab />}
        {tab === 'members' && <MembersTab />}
        {tab === 'deploy' && <DeployTab />}
        {tab === 'environment' && <EnvironmentTab />}
        {tab === 'alerts' && <AlertsTab />}
        {tab === 'bulk' && <BulkTab />}
      </div>
    </div>
  );
}
